import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { ContactForm } from '../components/ContactForm'
import { useCMSSide } from '../hooks/useCMS'

export function KlienthistoriePage() {
  const { side: cmsSide } = useCMSSide('klienthistorie')

  useSEO({
    title: cmsSide?.seo_titel || 'Sorgen gav slip ... | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Kropsterapi hjalp Charlotte til at bearbejde sorgen, sætte skjulte ressourcer fri og finde modet til at starte på drømme-uddannelsen',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/kvinde-i-sorg.jpg',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Klienthistorie
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'Sorgen gav slip ...'}
          </h1>
          <p className="mt-4 text-lg sm:text-xl font-serif text-[#1b3a4b]/90 max-w-2xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Kropsterapi hjalp Charlotte til at bearbejde sorgen, sætte skjulte ressourcer fri og finde modet til at starte på drømme-uddannelsen'}
          </p>
        </div>
      </section>

      {/* Main Story Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-10">
              {/* Charlotte Intro Profile */}
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] flex flex-col md:flex-row items-center gap-8">
            <div className="w-48 sm:w-56 flex-shrink-0">
              <div className="rounded-2xl overflow-hidden shadow-md border-2 border-[#E5E0D8]">
                <img
                  src="/_assets/charlotte-hoe-foto.jpg"
                  alt="Charlotte Hoé"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            <div className="space-y-3">
              <span className="text-xs font-semibold tracking-wider text-[#7c875a] uppercase block">
                Klientfortælling
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                Charlottes sorg sad gemt i hoften
              </h2>
              <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
                Charlotte kom til mig, på opfordring fra en veninde som selv havde prøvet at få kropsterapi.
              </p>
            </div>
          </div>

          {/* Hvad rygsækken er fyldt med */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Hvad ’rygsækken’ er flydt med
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Før den egentlige fysiske behandling startede vi med en indledende samtale hvor Charlotte fortalte hvad hun mærkede i kroppen, og hvilke tanker hun havde omkring hvilke sammenhænge det kunne ha’ med det som skete og det som var sket i hendes liv.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Fysisk havde hun smerter og ’muren’ i hoften, som hun mærkede konstant. Spændinger mærkede hun også i nakken og i skulderen, og så var der en udefinerbar uro og sitren i kroppen. Derudover var der ikke de store fysiske udfordringer.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              7 år idligere havde Charlotte mistede sin første mand helt uventet. Det var et kæmpe chok, og pludselig stod hun alene med 3 unge piger, hvoraf den yngste var på efterskole. Det var en meget voldsom oplevelse for dem alle, og situationen gjorde at hun holdt sammen på sig selv, og var stærk for sine piger og for sig selv. Hun fik god hjælp til at bearbejde sorgen med psykologhjælp, og støtte fra den øvrige familie og nære venner. En god proces hvor hun faktisk følte at hun havde fået bearbejdet den traumatiske oplevelse.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Efterfølgende har hun mødt en ny mand som hun blev gift med, og hun syntes faktisk at hun var kommet godt videre og havde et godt liv. Alligevel kunne hun godt mærke der lå noget gemt under overfladen, som hun ikke helt kunne sætte ord på.
            </p>
          </div>

          {/* Når kroppen fortæller */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Når kroppen fortæller
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Behandlingen startede med at få kroppen til at slappe af. Den pulserende massage, hvor hele kroppen kom i bevægelse, hjalp kroppen til at give slip, og med fokus på vejrtrækningen, begyndte nervesystemet at slippe spændingerne i kroppen.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Under første behandling havde Charlotte en oplevelse af at være ”ude af kroppen” – en følelse af at hun ligesom kunne se sig selv udefra. En lidt vild og overraskende oplevelse, som alligevel føltes rart og trygt. Under behandlingen blev Charlotte guidet tilbage og fik kontakt med oplevelsen omkring sin første mands dødsfald.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ved anden behandling var Charlotte, lidt mere forberedt på hvad der skulle ske under behandlingen. Kroppen slappede mere af og var indstillet på at møde det som ville komme. Med fokus på at hjælpe kroppen til at slippe spændingen i venstre hofte, blev kroppen pulseret med store bevægelser fra side til side. Kroppen begyndte at reagere
            </p>
            <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
              Tryghed er det vigtigste for en god behandling, som altid tilpasses til der hvor klienten er, og er klar til at modtage. Processen kan og skal ikke forceres. På samme måde som man ikke kan få en plante til at vokse hurtigere ved at trække i den, kan kroppen ikke slippe en spænding før den er klar til det.
            </blockquote>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              ved at spænde mere op, og ved guidet vejrtrækning og tilpasning af behandlingen, så det hele tiden mødte Charlotte med omsorg, blev spændingerne stille og roligt sluppet ud af kroppen. Efter behandlingen fortalte Charlotte at hun følte sin afdøde mands kærlighed og nærhed, og at han fortalte hende at det var okay at give slip. En meget bevægende oplevelse.
            </p>
          </div>

          {/* En voldsom reaktion – en fantastisk befrielse */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              En voldsom reaktion – en fantastisk befrielse
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Natten efter sov hun meget dårligt, og havde kvalme og kastede op – og hun var faktisk ikke sikker på om hun ville komme igen. Heldigvis kontaktede hun mig og vi fik en snak om kroppens reaktion og den udrensning som var sat i gang. Vi aftalt en tid til akut behandling. Da hun kom dagen efter, var svimmelheden aftaget og kvalmen var væk. Kroppens reaktion havde overrasket hende, men hun var sikker på at det var det hun havde haft brug for. Med en blid behandling hvor fokus var på at få kroppe til at slappe af og skabe jordforbindelse, blev der skabt ro i kroppen igen.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ved den efterfølgende behandling fortalte Charlotte at hun tidligere havde været meget lysfølsom, og at hun i flere år havde sovet med øjenmaske – den havde hun nu ikke haft brug for i de seneste 14 dage. Vejrtrækningen var blevet mere fri, hvilket hun mærkede ved at maven bevægede sig mere når hun trak vejret. Smerterne i hoften var blevet meget mindre.
            </p>
            <blockquote className="border-l-4 border-[#7c875a] bg-[#FAF8F5] p-5 rounded-r-lg text-sm sm:text-base italic text-[#334155] leading-relaxed">
              Når kroppen slipper gamle spændinger og beskyttende lag, sættes en udrensende reaktion i gang. Det kan i nogle tilfælde give kvalme og opkast, hvilket dog er sjældent. Ofte mærkes lettere fysiske reaktioner som kortvarig hovedpine og følelses-mæssige reaktioner som ’ked-af-det-hed’, når kroppen giver slip på uforløste hændelser fra tidligere oplevelser i livet.
            </blockquote>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Under behandlingen mærkede hun at højre fod og hele maveregionen var meget spændt. Lysken blev afspændt og mere fleksibel undervejs. Spændingerne gjorde at vejrtrækningen blev mere anstrengt. Behandling af ribbenene og Charlottes egen fokus på vejrtrækningen skabte mere plads og luft, som hjalp kroppen til at slippe spændingerne.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Efter behandlingen fortalte Charlotte at hun undervejs i behandlingen havde fået et billede af at der ’fløj skidt ud af hendes mund’ – et billede som hun genkendte fra filmen ”Den grønne mil” – igen en overraskende oplevelse, der føltes som at kroppen gav slip på noget.
            </p>
          </div>

          {/* Coachende behandling – på vej mod fremtiden */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-4">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              Coachende behandling – på vej mod fremtiden
            </h3>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ved næste behandling kom Charlotte med meget energi i kroppen, og en boblende glæde indeni. Hun fortæller glad at hun nu synger højt i bilen, og føler sig fri.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Under behandlingen flyttes fokus over på at se fremad. Kombinationen af den fysiske behandling og coaching til at mærke efter inden i, åbner op og nye indsigter på egne kompetencer og muligheder kommer op til overfladen. Undervejs i behandlingen kommer vi ind på hvad det er som begrænser os i vores liv, og erkendelsens lys bliver tændt da Charlotte mærker ressourcer som hun ikke tidligere har være bevist om. Ressourcer som hun på sin vis godt vidste hun besidder, men aldrig rigtig har levet ud.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Efter behandlingen mærker Charlotte glæden flyde frit og en indre energi er blevet tændt.
            </p>
            <p className="text-sm sm:text-base text-[#475569] leading-relaxed">
              Ved den sjette behandling kommer Charlotte glad og med god energi i kroppen. Efter en kort gang influenza siden sidste behandling, er hun nu klar igen. Kroppen har været hurtig til at komme i balance og energien er tilbage. Jobbet som køkkenleder på Strib efterskole, er lidt stressende her i starten af skoleåret, men hun ved at det kun er en overgang så alt er fint. Hun trives godt i en hverdag med tæt kontakt til eleverne. Hun fortæller glad hvordan en af eleverne sagde til hende at han godt kunne se at hun var glad for sit arbejde.
            </p>
          </div>

          {/* Et behandlingsforløb er slut */}
          <div className="bg-[#1b3a4b] text-white rounded-2xl p-8 sm:p-10 shadow-xl space-y-4">
            <h3 className="font-serif text-2xl font-bold text-white">
              Et behandlingsforløb er slut
            </h3>
            <p className="text-sm sm:text-base text-[#FAF8F5]/90 leading-relaxed">
              Efter bare 6 behandlinger er kroppen blevet mere smidig og energien flyder. Smerterne i hoften er væk, og spændingerne i nakke og skulderne har sluppet deres tag. Et fastlåst traume er blevet forløst. Selvindsigt har skærpet opmærksomheden og givet forståelse for indre ressourcer og kompetencer. Fremtiden ligger forude med nye udfordringer og nye muligheder og nye valg.
            </p>
          </div>

          {/* 4 Måneder efter - Spørgsmål og Svar */}
          <div className="bg-white rounded-2xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
            <h3 className="font-serif text-2xl font-bold text-[#1b3a4b]">
              4 måneder efter Charlottes behandlingsforløb
            </h3>
            <p className="text-sm text-[#64748B]">
              Bo bad Charlotte om at give sin feedback på hvordan hun oplevede behandlingen, og hvilke forandringer hun efterfølgende har oplevet:
            </p>

            <div className="space-y-6 text-sm sm:text-base">
              <div className="border-l-2 border-[#1b3a4b] pl-4 space-y-1">
                <strong className="text-[#1b3a4b] block">Har behandlingen hjulpet dig med de udfordringer du søgte hjælp til?</strong>
                <p className="text-[#475569] italic">
                  Ja min krop er blevet lidt lettere og jeg kan bedre være i “mig”. Hvor jeg tidligere ikke kunne være i ro til at lave yoga, gør jeg nu det 2-4 gange i ugen. Men mærker nu uroen under huden og ved at kroppen trænger til en omgang igen
                </p>
              </div>

              <div className="border-l-2 border-[#1b3a4b] pl-4 space-y-1">
                <strong className="text-[#1b3a4b] block">Levede behandlingen op til dine forventninger? - Eller var kropsterapi en helt anden oplevelse end du havde forestillet dig?</strong>
                <p className="text-[#475569] italic">
                  Behandlingerne levede absolut op til mine forventninger og nok også mere til. Jeg tror at jeg den gang syntes det var anderledes end jeg havde forestillet mig, men igen kun positivt også trods den voldsomme gang hvor jeg virkeligt rensede ud. (Se ovenfor)
                </p>
              </div>

              <div className="border-l-2 border-[#1b3a4b] pl-4 space-y-1">
                <strong className="text-[#1b3a4b] block">Hvordan oplever du kombinationen af fysisk behandling og samtale? Skal der mere fokus på den ene eller den anden del? – eller er fordelingen tilpas?</strong>
                <p className="text-[#475569] italic">
                  Kombinationen tænker jeg er rigtig fint og falder ret naturligt. Det kunne jeg godt lide.
                </p>
              </div>

              <div className="border-l-2 border-[#1b3a4b] pl-4 space-y-1">
                <strong className="text-[#1b3a4b] block">Var der erindringer fra tidligere oplevelser som dukkede op under behandlingen</strong>
                <p className="text-[#475569] italic">
                  Jeg mindes at min afdøde mand var til stede, og jeg forsvinder også lidt væk. Men det er et svært spørgsmål
                </p>
              </div>

              <div className="border-l-2 border-[#1b3a4b] pl-4 space-y-1">
                <strong className="text-[#1b3a4b] block">I min behandling bruger jeg ofte coaching til at synliggøre de ressourcer som ligger gemt under overfladen. Hvordan oplever du den del af behandlingen?</strong>
                <p className="text-[#475569] italic">
                  Mht. at du synliggør ens egne ressourcer er det helt vildt hvad det satte gang i hos mig. Nogle tanker nåede jeg at dele med dig, men faktum er at jeg har tilmeldt mig NLP psykoterapeut i Aarhus og skal starte d. 27. Januar 2021
                </p>
              </div>

              <div className="border-l-2 border-[#1b3a4b] pl-4 space-y-1">
                <strong className="text-[#1b3a4b] block">Har behandlingen skabt forandringer ”inde i dig”? Har behandlingen givet dig nye indsigter og forståelse?</strong>
                <p className="text-[#475569] italic">
                  Forandringer, ja vel - og ja tak til selvudvikling og papir på det jeg kan (dine pæne ord ). Jeg har jo vidst at folk er den de er, pga. indholdet i rygsækkene. Jeg har nok fået en endnu bedre forståelse der. Og en egen indsigt i hvad jeg, og min krop behøver.
                </p>
              </div>

                <div className="pt-4 text-center">
                  <span className="font-serif text-lg font-bold text-[#1b3a4b] block">
                    Jeg har en stor taknemmelighed til dig.
                  </span>
                  <span className="text-sm text-[#7c875a] font-medium block mt-1">
                    Kærlig hilsen Charlotte
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Aside Contact Form */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24 space-y-6">
              <ContactForm />
              <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] text-xs text-[#475569] space-y-2.5 shadow-sm">
                <div className="font-semibold text-[#1b3a4b] text-sm">Direkte kontakt</div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#7c875a]" aria-hidden="true" />
                  <span>Vendersgade 37, 7000 Fredericia</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-[#7c875a]" aria-hidden="true" />
                  <a href="tel:53128213" className="hover:text-[#1b3a4b] font-medium">Tlf: 53 12 82 13</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
